import type { Deposit, DaySummary, UndepositedPayment } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { DecoRule, OakMark, Wordmark } from '../components/brand';
import { Empty, ErrorBox, PageHead, PracticeBanner, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatStamp, isIsoDate, parseTyped, todayIso } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';

/** One check spread over invoices is one check at the bank: the rows of a group shown and deposited together. */
export function slipLines(rows: UndepositedPayment[]): { key: string; ids: string[]; first: UndepositedPayment; amount: number; invoices: number[] }[] {
  const out = new Map<string, { key: string; ids: string[]; first: UndepositedPayment; amount: number; invoices: number[] }>();
  for (const r of rows) {
    const k = r.groupId ?? r.paymentId;
    const cur = out.get(k) ?? { key: k, ids: [], first: r, amount: 0, invoices: [] };
    cur.ids.push(r.paymentId);
    cur.amount = Math.round((cur.amount + r.amount) * 100) / 100;
    if (r.invoiceNo !== null) cur.invoices.push(r.invoiceNo);
    out.set(k, cur);
  }
  return [...out.values()];
}

/** The end of the day (decision 105): the day's money, the deposit for the bank, and the day closed. */
export function EndOfDayPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [dateRaw, setDate] = useSearchParam('date');
  const date = isIsoDate(dateRaw) ? dateRaw : todayIso();
  const day = useQuery({ queryKey: ['money', 'day', date], queryFn: ({ signal }) => api.moneyDay(date, signal) });
  const waiting = useQuery({ queryKey: ['money', 'undeposited'], queryFn: ({ signal }) => api.undeposited(signal) });
  const deposits = useQuery({ queryKey: ['money', 'deposits'], queryFn: ({ signal }) => api.deposits(signal) });
  const lines = useMemo(() => slipLines(waiting.data ?? []), [waiting.data]);
  const [left, setLeft] = useState<Set<string>>(new Set());
  const [account, setAccount] = useState('');
  const chosen = lines.filter((l) => !left.has(l.key));
  const depositKey = useRef<{ body: string; key: string } | null>(null);
  const deposit = useMutation({
    mutationFn: () => {
      const body = { paymentIds: chosen.flatMap((l) => l.ids), depositedOn: todayIso(), ...(account.trim() ? { account: account.trim() } : {}) };
      const serialized = JSON.stringify(body);
      if (depositKey.current?.body !== serialized) depositKey.current = { body: serialized, key: newIdempotencyKey() };
      return api.makeDeposit(body, depositKey.current.key);
    },
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: (d) => {
      depositKey.current = null;
      void qc.invalidateQueries({ queryKey: ['money'] });
      navigate(`/money/deposits/${d.id}`);
    },
    // someone else may have deposited part of it: show what still waits
    onError: () => void qc.invalidateQueries({ queryKey: ['money', 'undeposited'] }),
  });
  const cashIn = chosen.filter((l) => l.first.method === 'cash').reduce((n, l) => n + l.amount, 0);
  const checksIn = chosen.filter((l) => l.first.method === 'check').reduce((n, l) => n + l.amount, 0);

  return (
    <div className="stack">
      <PageHead title={t('endOfDayTitle')} back="/" />
      <PracticeBanner />
      <div className="day-nav">
        <button type="button" className="btn btn-ghost" onClick={() => setDate(addDays(date, -1))} aria-label={t('prevDay')}>
          ‹
        </button>
        <strong>{formatDate(date, intlLocale, 'long')}</strong>
        <button type="button" className="btn btn-ghost" onClick={() => setDate(addDays(date, 1))} aria-label={t('nextDay')} disabled={date >= todayIso()}>
          ›
        </button>
      </div>
      <QueryView query={day}>{(d: DaySummary) => <DayCard day={d} />}</QueryView>

      <section className="card stack-sm" data-testid="undeposited">
        <h2>{t('forTheBank')}</h2>
        {/* a route turned in less cash than it collected: the payments below are what the customers paid, the bank
            gets less (end-user test: a $45 turn-in of $50 still showed $50 for the bank) */}
        {(day.data?.routes ?? []).filter((r) => r.variance !== null && r.variance <= -0.005).map((r) => (
          <p key={r.routeId} className="warn-band" role="alert" data-testid="route-cash-short">
            {t('routeCashShortBank').replaceAll('{route}', r.name).replaceAll('{short}', formatMoney(-r.variance!, intlLocale)).replaceAll('{collected}', formatMoney(r.cash, intlLocale)).replaceAll('{counted}', formatMoney(r.turnedInAmount ?? 0, intlLocale))}
          </p>
        ))}
        <QueryView query={waiting}>
          {() =>
            lines.length === 0 ? (
              <Empty>{t('nothingToDeposit')}</Empty>
            ) : (
              <>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th />
                        <th>{t('received')}</th>
                        <th>{t('customer')}</th>
                        <th>{t('method')}</th>
                        <th className="num">{t('amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => (
                        <tr key={l.key}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`${t('inDeposit')} ${l.first.customerName} ${l.amount}`}
                              checked={!left.has(l.key)}
                              onChange={(e) => {
                                const next = new Set(left);
                                if (e.target.checked) next.delete(l.key);
                                else next.add(l.key);
                                setLeft(next);
                              }}
                            />
                          </td>
                          <td>{formatStamp(l.first.receivedAt, intlLocale, 'date')}</td>
                          <td>
                            {l.first.customerName}
                            {l.first.routeName ? <span className="muted"> · {l.first.routeName}</span> : null}
                          </td>
                          <td>
                            {t(`method_${l.first.method}`)}
                            {l.first.reference ? ` ${l.first.reference}` : ''}
                          </td>
                          <td className="num">{formatMoney(l.amount, intlLocale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <dl className="facts plain">
                  <div className="fact">
                    <dt>{t('method_cash')}</dt>
                    <dd>{formatMoney(cashIn, intlLocale)}</dd>
                  </div>
                  <div className="fact">
                    <dt>{t('checks')}</dt>
                    <dd>{formatMoney(checksIn, intlLocale)}</dd>
                  </div>
                  <div className="fact">
                    <dt>{t('total')}</dt>
                    <dd className="money" data-testid="deposit-total">{formatMoney(cashIn + checksIn, intlLocale)}</dd>
                  </div>
                </dl>
                <label className="field">
                  <span>{t('bankAccount')}</span>
                  <input type="text" value={account} maxLength={60} autoComplete="off" onChange={(e) => setAccount(e.target.value)} />
                </label>
                {deposit.error ? <ErrorBox error={deposit.error} /> : null}
                <button type="button" className="btn btn-primary btn-lg btn-block" disabled={deposit.isPending || chosen.length === 0} onClick={() => deposit.mutate()}>
                  {t('makeDeposit')}
                </button>
              </>
            )
          }
        </QueryView>
      </section>

      <section className="card stack-sm">
        <h2>{t('depositsMade')}</h2>
        <QueryView query={deposits}>
          {(ds) =>
            ds.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {ds.slice(0, 20).map((x) => (
                  <li key={x.id}>
                    <Link className="row" to={`/money/deposits/${x.id}`}>
                      <span className="row-main">
                        <span className="row-title">
                          {t('deposit')} {x.depositNo}
                        </span>
                        <span className="row-sub">
                          {formatDate(x.depositedOn, intlLocale, 'date')} · {x.count} · {x.createdBy ?? '—'}
                        </span>
                      </span>
                      <span className="row-end-value">{formatMoney(x.total, intlLocale)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )
          }
        </QueryView>
      </section>
    </div>
  );
}

function DayCard({ day: d }: { day: DaySummary }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const close = useMutation({
    mutationFn: (cash: number | null) => api.closeDay({ date: d.date, ...(cash !== null ? { cashCounted: cash } : {}), ...(note.trim() ? { note: note.trim() } : {}) }),
    onSuccess: (s) => qc.setQueryData(['money', 'day', d.date], s),
  });
  return (
    <section className="card stack-sm" data-testid="day">
      <dl className="facts plain">
        <div className="fact">
          <dt>{t('invoicesTitle')}</dt>
          <dd>
            {d.invoices.count} · {formatMoney(d.invoices.total, intlLocale)}
          </dd>
        </div>
        {d.credits > 0 ? (
          <div className="fact">
            <dt>{t('credits')}</dt>
            <dd>{formatMoney(d.credits, intlLocale)}</dd>
          </div>
        ) : null}
        {d.received.map((r) => (
          <div key={r.method} className="fact">
            <dt>{t(`method_${r.method}` as AppMessageKey)}</dt>
            <dd>{formatMoney(r.amount, intlLocale)}</dd>
          </div>
        ))}
        <div className="fact">
          <dt>{t('receivedTotal')}</dt>
          <dd className="money">{formatMoney(d.receivedTotal, intlLocale)}</dd>
        </div>
      </dl>
      {d.routes.length > 0 ? (
        <div className="table-wrap">
          <table className="table" data-testid="day-routes">
            <thead>
              <tr>
                <th>{t('routesTitle')}</th>
                <th className="num">{t('method_cash')}</th>
                <th className="num">{t('checks')}</th>
                <th className="num">{t('turnedIn')}</th>
                <th className="num">{t('variance')}</th>
              </tr>
            </thead>
            <tbody>
              {d.routes.map((r) => (
                <tr key={r.routeId}>
                  <td>
                    {r.name}
                    {r.driverName ? <span className="muted"> · {r.driverName}</span> : null}
                  </td>
                  <td className="num">{formatMoney(r.cash, intlLocale)}</td>
                  <td className="num">{formatMoney(r.checks, intlLocale)}</td>
                  <td className="num">{r.turnedInAmount === null ? <span className="badge badge-warn">{t('notTurnedIn')}</span> : formatMoney(r.turnedInAmount, intlLocale)}</td>
                  <td className={r.variance !== null && Math.abs(r.variance) >= 0.005 ? 'num owed' : 'num'}>{r.variance === null ? '—' : formatMoney(r.variance, intlLocale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {d.stopsNotDelivered > 0 || d.deliveredNotInvoiced > 0 ? (
        <p className="note" data-testid="day-open">
          {d.stopsNotDelivered > 0 ? `${d.stopsNotDelivered} ${t('stopsNotDelivered')}` : ''}
          {d.stopsNotDelivered > 0 && d.deliveredNotInvoiced > 0 ? ' · ' : ''}
          {d.deliveredNotInvoiced > 0 ? `${d.deliveredNotInvoiced} ${t('deliveredNotInvoiced')}` : ''}
        </p>
      ) : null}
      {d.closed ? (
        <p className="flash" role="status" data-testid="day-closed">
          ✓ {t('dayClosed')} · {d.closed.by ?? '—'} · {formatStamp(d.closed.at, intlLocale)}
          {d.closed.cashCounted !== null ? ` · ${t('cashCounted')} ${formatMoney(d.closed.cashCounted, intlLocale)}` : ''}
          {d.closed.note ? ` · ${d.closed.note}` : ''}
          {d.changedSinceClose ? <span className="badge badge-warn">{t('changedSinceClose')}</span> : null}
        </p>
      ) : (
        <form
          className="contact-actions"
          onSubmit={(e) => {
            e.preventDefault();
            const n = counted.trim() === '' ? null : parseTyped(counted);
            if (counted.trim() !== '' && (n === null || n < 0)) return setProblem('typedNotNumber');
            setProblem(null);
            close.mutate(n === null ? null : Math.round(n * 100) / 100);
          }}
        >
          <label className="field" style={{ flex: 1 }}>
            <span>{t('cashCounted')}</span>
            <input type="text" inputMode="decimal" autoComplete="off" value={counted} aria-invalid={problem !== null || undefined} onChange={(e) => setCounted(e.target.value)} placeholder="$" />
          </label>
          <label className="field" style={{ flex: 2 }}>
            <span>{t('notes')}</span>
            <input type="text" autoComplete="off" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-secondary" disabled={close.isPending}>
            {t('closeDay')}
          </button>
        </form>
      )}
      {problem ? (
        <p className="field-error" role="alert">
          {t(problem)}
        </p>
      ) : null}
      {close.error ? <ErrorBox error={close.error} /> : null}
    </section>
  );
}

/** The deposit slip: the bank's copy of what went in, checks listed with their numbers, printable. */
export function DepositSlipPage() {
  const { t, intlLocale } = useLocale();
  const { id = '' } = useParams();
  const query = useQuery({ queryKey: ['money', 'deposit', id], queryFn: ({ signal }) => api.deposit(id, signal) });
  return (
    <QueryView query={query}>
      {(d: Deposit) => {
        const checks = slipLines(d.items.filter((i) => i.method === 'check'));
        return (
          <div className="stack invoice">
            <PageHead title={`${t('deposit')} ${d.depositNo}`} back="/money/day" />
            <header className="letterhead print-only">
              <OakMark />
              <Wordmark />
              <DecoRule />
            </header>
            <section className="card stack-sm" data-testid="deposit-slip">
              <h2>
                {t('depositSlip')} {d.depositNo}
              </h2>
              <p className="muted">
                {formatDate(d.depositedOn, intlLocale, 'long')}
                {d.account ? ` · ${d.account}` : ''} · {d.createdBy ?? '—'}
              </p>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('checkNo')}</th>
                      <th>{t('customer')}</th>
                      <th className="num">{t('amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checks.map((c) => (
                      <tr key={c.key}>
                        <td>{c.first.reference ?? '—'}</td>
                        <td>{c.first.customerName}</td>
                        <td className="num">{formatMoney(c.amount, intlLocale)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}>
                        {t('checks')} ({checks.length})
                      </td>
                      <td className="num">{formatMoney(d.checks, intlLocale)}</td>
                    </tr>
                    <tr>
                      <td colSpan={2}>{t('method_cash')}</td>
                      <td className="num">{formatMoney(d.cash, intlLocale)}</td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <strong>{t('total')}</strong>
                      </td>
                      <td className="num">
                        <strong>{formatMoney(d.total, intlLocale)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {d.note ? <p className="pre">{d.note}</p> : null}
              <button type="button" className="btn btn-primary btn-block no-print" onClick={() => window.print()}>
                {t('print')}
              </button>
            </section>
          </div>
        );
      }}
    </QueryView>
  );
}
